import { PAGES } from "./constants";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import {
  ArrowDropDown,
  ExitToApp,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
} from "@mui/material";
import { useAuth } from "../Hooks/AuthHook";
import { UserButton } from "@clerk/clerk-react";
import { useState } from "react";
import logo from "../logo.png";
import "./Navibar.css";

const NAV_LINKS: { to: string; label: string }[] = [
  { to: PAGES.QUANTIFIER, label: "Quantifiers" },
  { to: PAGES.TESTING_BOARDS, label: "Testing Boards" },
  { to: PAGES.CALIBRATION, label: "Calibration" },
  { to: PAGES.HISTORY, label: "Board History" },
  { to: PAGES.FAILED_SENSORS, label: "Failed Sensors" },
  { to: PAGES.DEPLOYMENTS, label: "Deployments" },
  { to: PAGES.GATEWAYS, label: "Gateways" },
  { to: PAGES.SITES, label: "Sites" },
  { to: PAGES.USERS, label: "Users" },
  { to: PAGES.PLANS_OPTIMIZATION, label: "Plans Optimization" },
];

function Navibar() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [notesAnchorEl, setNotesAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const notesOpen = Boolean(notesAnchorEl);
  const mobileMenuOpen = Boolean(mobileMenuAnchor);

  const handleNotesClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotesAnchorEl(event.currentTarget);
  };
  const handleNotesClose = () => {
    setNotesAnchorEl(null);
  };
  const handleNotesManage = () => {
    navigate(PAGES.MANAGE);
    handleNotesClose();
  };
  const handleNotesSiteField = () => {
    navigate(PAGES.NOTES_SITE_FIELD);
    handleNotesClose();
  };
  const handleNotesDistributors = () => {
    navigate(PAGES.NOTES_DISTRIBUTORS);
    handleNotesClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Sign out failed. Please try again.";
      setSignOutError(message);
    }
  };

  const navContent = (
    <>
      {NAV_LINKS.map(({ to, label }) => (
        <Button
          component={Link}
          key={to}
          to={to}
          sx={{
            color:
              pathname === to || pathname.startsWith(to + "/")
                ? "primary.contrastText"
                : "inherit",
            textDecoration:
              pathname === to || pathname.startsWith(to + "/")
                ? "underline"
                : "none",
            "&:hover": {
              color: "inherit",
              backgroundColor: "rgba(255,255,255,0.08)",
              textDecoration: "none",
            },
          }}
        >
          {label}
        </Button>
      ))}
      <Button
        color="inherit"
        onClick={handleNotesClick}
        id="notes-dropdown"
        aria-controls={notesOpen ? "notes-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={notesOpen ? "true" : undefined}
        className="color-grey"
        endIcon={
          <ArrowDropDown
            sx={{
              transform: notesOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        }
      >
        Field
      </Button>
      <Menu
        id="notes-menu"
        anchorEl={notesAnchorEl}
        open={notesOpen}
        onClose={handleNotesClose}
        MenuListProps={{ "aria-labelledby": "notes-dropdown" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleNotesManage}>Crew</MenuItem>
        <MenuItem onClick={handleNotesSiteField}>Notes</MenuItem>
        <MenuItem onClick={handleNotesDistributors}>Distributors</MenuItem>
      </Menu>
    </>
  );

  return (
    <>
      <AppBar position="fixed">
        <Container maxWidth={false}>
          <Toolbar
            disableGutters
            sx={{ justifyContent: "space-between", flexWrap: "wrap" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                color="inherit"
                aria-label="open navigation menu"
                onClick={handleMobileMenuOpen}
                sx={{ display: { xs: "block", lg: "none" } }}
              >
                <MenuIcon />
              </IconButton>
              <Button
                component={Link}
                to={PAGES.QUANTIFIER}
                sx={{
                  color: "inherit",
                  textTransform: "none",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  gap: 1,
                  "&:hover": {
                    color: "inherit",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    textDecoration: "none",
                  },
                }}
                startIcon={
                  <Box
                    component="img"
                    src={logo}
                    alt=""
                    sx={{ height: 32, display: "block" }}
                  />
                }
              >
                LiORA Internal
              </Button>
            </Box>

            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {navContent}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <UserButton />
              <Button
                color="inherit"
                onClick={handleSignOut}
                endIcon={<ExitToApp />}
                sx={{ textTransform: "none" }}
              >
                Sign out
              </Button>
            </Box>
          </Toolbar>
        </Container>

        <Menu
          anchorEl={mobileMenuAnchor}
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{ display: { xs: "block", lg: "none" } }}
        >
          {NAV_LINKS.map(({ to, label }) => (
            <MenuItem
              key={to}
              onClick={() => {
                navigate(pathname === to ? { pathname: to, search } : to);
                handleMobileMenuClose();
              }}
            >
              {label}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              handleNotesManage();
              handleMobileMenuClose();
            }}
          >
            Field – Crew
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleNotesSiteField();
              handleMobileMenuClose();
            }}
          >
            Field – Notes
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleNotesDistributors();
              handleMobileMenuClose();
            }}
          >
            Field – Distributors
          </MenuItem>
        </Menu>
      </AppBar>
      <Toolbar /> {/* Spacer so content is not hidden under fixed AppBar */}
      <Outlet />
      <Snackbar
        open={signOutError !== null}
        autoHideDuration={6000}
        onClose={() => setSignOutError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSignOutError(null)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {signOutError}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Navibar;
